import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Calendar as CalendarIcon, Check, Loader2, CalendarDays, Car, Sparkles, CreditCard, Wallet, Info, Tag } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DataBoundary } from '../components/common/DataBoundary';
import { useToast } from '../hooks/use-toast';

function BookingPage({ user, setUser }) {
// Static stillingar fyrir viðbótarþjónustu miðað við aðalþjónustu
// lykill = service.id úr /api/services
const EXTRA_SERVICE_CONFIG = {
  althrif: ['lakkvorn_1ar', 'djuphreinsun', 'ozone_medferd'],
  soluthrif: ['lakkvorn_1ar', 'djuphreinsun', 'blettun_lakk'],
  thrif_utan_bon: ['lakkvorn_1ar', 'jarnhreinsun'],
  thrif_innan: ['djuphreinsun', 'djuph_loftklædning', 'mygluhreinsun', 'ozone_medferd'],
  althrif_djuphreinsun: ['lakkvorn_1ar', 'ozone_medferd'],
};

// MOCK: Bundle discount config (þegar CRM er tilbúið kemur þetta frá API)
// Þetta eru dæmi um afslátt fyrir test
const MOCK_BUNDLE_DISCOUNTS = {
  althrif: {
    discount_percent: 10,
    discount_message: "10% afsláttur af viðbótarþjónustum þegar bætt við Alþrif"
  },
  soluthrif: {
    discount_percent: 8,
    discount_message: "8% afsláttur af viðbótarþjónustum"
  }
};


  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(location.state?.selectedService?.id || '');
  const [extraServices, setExtraServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // Changed to Date object for Calendar
  const [selectedTime, setSelectedTime] = useState('');
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [vehicleLookupError, setVehicleLookupError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingStatus, setPricingStatus] = useState('idle'); // idle | loading | ok | not_found | error
  const [vehicleSize, setVehicleSize] = useState('');
  const [extraServicePricing, setExtraServicePricing] = useState({}); // { serviceId: { price, status } }
  const [formData, setFormData] = useState({
    kennitala: '',
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    plate: '',
    vehicle: '',
    ownerName: '', // From vehicle lookup
    isOwner: null, // true if customer === owner
    notes: '',
    paymentMethod: 'on_site'
  });
  const [lookingUpPlate, setLookingUpPlate] = useState(false);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD
      fetchAvailability(dateStr);
    }
  }, [selectedDate]);

  // Heildarvalin þjónusta (aðal + viðbótarþjónustur)
  const selectedServices = [selectedService, ...extraServices].filter(Boolean);

  useEffect(() => {
    // Fetch pricing when aðalþjónusta og bílastærð eru valin
    if (selectedService && vehicleSize) {
      fetchPricing(selectedService, vehicleSize);
    }
  }, [selectedService, vehicleSize]);

  // Fetch pricing for extra services when they're shown
  useEffect(() => {
    if (selectedService && vehicleSize && EXTRA_SERVICE_CONFIG[selectedService]) {
      const extraServiceIds = EXTRA_SERVICE_CONFIG[selectedService];
      extraServiceIds.forEach((serviceId) => {
        fetchExtraServicePricing(serviceId, vehicleSize);
      });
    }
  }, [selectedService, vehicleSize]);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`);
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchAvailability = async (date) => {
    setLoadingAvailability(true);
    setAvailabilityError(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/availability?date=${date}`);
      if (!response.ok) {
        throw new Error('Tókst ekki að sækja lausa tíma');
      }
      const data = await response.json();
      setAvailability(data.slots || []);
      setAvailabilityError(false);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailability([]);
      setAvailabilityError(true);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const fetchPricing = async (serviceId, size) => {
    setPricingStatus('loading');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/${serviceId}/pricing?vehicle_size=${size}`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
        setPricingStatus('ok');
      } else if (response.status === 404) {
        setPricing(null);
        setPricingStatus('not_found');
      } else {
        setPricing(null);
        setPricingStatus('error');
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setPricing(null);
      setPricingStatus('error');
    }
  };

  const fetchExtraServicePricing = async (serviceId, size) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/${serviceId}/pricing?vehicle_size=${size}`);
      if (response.ok) {
        const data = await response.json();
        setExtraServicePricing((prev) => ({
          ...prev,
          [serviceId]: { price: data.price, status: 'ok' }
        }));
      } else if (response.status === 404) {
        setExtraServicePricing((prev) => ({
          ...prev,
          [serviceId]: { price: null, status: 'not_found' }
        }));
      } else {
        setExtraServicePricing((prev) => ({
          ...prev,
          [serviceId]: { price: null, status: 'error' }
        }));
      }
    } catch (error) {
      console.error('Error fetching extra service pricing:', error);
      setExtraServicePricing((prev) => ({
        ...prev,
        [serviceId]: { price: null, status: 'error' }
      }));
    }
  };

  const toggleExtraService = (id) => {
    setExtraServices((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id]
    );
  };

  // Reikna heildarverð (aðalþjónusta + valdar viðbótarþjónustur)
  const calculateTotalPrice = () => {
    let subtotal = 0;
    let mainServicePrice = 0;
    let hasAllPrices = true;
    let extraServicesSubtotal = 0;

    // Aðalþjónusta (alltaf fullt verð)
    if (pricing && pricing.price) {
      mainServicePrice = pricing.price;
      subtotal += pricing.price;
    } else {
      hasAllPrices = false;
    }

    // Valdar viðbótarþjónustur
    extraServices.forEach((serviceId) => {
      const extraPrice = extraServicePricing[serviceId];
      if (extraPrice && extraPrice.price) {
        subtotal += extraPrice.price;
        extraServicesSubtotal += extraPrice.price;
      } else {
        hasAllPrices = false;
      }
    });

    // MOCK: Reikna discount (ef til staðar)
    let discount = 0;
    let discountPercent = 0;
    let discountMessage = '';
    
    const bundleDiscount = MOCK_BUNDLE_DISCOUNTS[selectedService];
    if (bundleDiscount && extraServices.length > 0) {
      discountPercent = bundleDiscount.discount_percent;
      // Discount á viðbótarþjónustur aðeins (ekki aðalþjónustu)
      discount = Math.round(extraServicesSubtotal * (discountPercent / 100));
      discountMessage = bundleDiscount.discount_message;
    }

    const finalTotal = subtotal - discount;

    return { 
      subtotal, 
      discount, 
      discountPercent,
      discountMessage,
      finalTotal, 
      hasAllPrices,
      mainServicePrice,
      extraServicesSubtotal
    };
  };


  const handleKennitalaChange = async (e) => {
    const kt = e.target.value.replace(/\D/g, ''); // Only numbers
    setFormData(prev => ({...prev, kennitala: kt}));
    
    // Lookup customer when kennitala is 10 digits
    if (kt.length === 10) {
      setLookingUpCustomer(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-lookup?kennitala=${kt}`);
        if (response.ok) {
          const data = await response.json();
          if (data.found) {
            setFormData(prev => {
              const newData = {
                ...prev,
                name: data.name || prev.name,
                phone: data.phone || prev.phone,
                email: data.email || prev.email
              };
              
              // Check if this customer is the owner
              if (prev.ownerName && data.name) {
                const isOwner = data.name.toLowerCase().trim() === prev.ownerName.toLowerCase().trim();
                newData.isOwner = isOwner;
                
                if (isOwner) {
                  toast({
                    title: "Skráður eigandi",
                    description: `${data.name} er skráður eigandi bílsins`,
                  });
                }
              } else if (!data.found) {
                toast({
                  title: "Viðskiptavinur fannst ekki",
                  description: "Enginn viðskiptavinur fannst með þessa kennitölu.",
                  variant: "destructive"
                });
              }
              
              return newData;
            });
          } else {
            toast({
              title: "Viðskiptavinur fannst ekki",
              description: "Enginn viðskiptavinur fannst með þessa kennitölu.",
              variant: "destructive"
            });
          }
        }
      } catch (error) {
        console.error('Customer lookup error:', error);
      } finally {
        setLookingUpCustomer(false);
      }
    }
  };

  const handlePlateBlur = async () => {
    const plate = formData.plate;
    
    // Auto-fill phone from previous bookings with this plate
    if (plate.length >= 3) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/lookup-by-license-plate?plate=${plate}`);
        if (response.ok) {
          const data = await response.json();
          if (data.found && data.customer_phone) {
            // Only autofill if phone is empty
            if (!formData.phone) {
              setFormData(prev => ({
                ...prev,
                phone: data.customer_phone
              }));
              
              toast({
                title: "Símanúmer fundið",
                description: "Símanúmer fyllt út sjálfkrafa úr fyrri bókun",
              });
            }
          }
        }
      } catch (error) {
        console.error('Booking lookup error:', error);
        // Silent fail - this is just a convenience feature
      }
    }
  };

  const handlePlateChange = async (e) => {
    const plate = e.target.value.toUpperCase();
    setFormData(prev => ({...prev, plate: plate}));
    
    // Lookup vehicle when plate is 3+ characters
    if (plate.length >= 3) {
      setLookingUpPlate(true);
      setVehicleLookupError(false);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-lookup?plate=${plate}`);
        if (response.ok) {
          const data = await response.json();
          if (data.found) {
            setFormData(prev => {
              const newData = {
                ...prev, 
                vehicle: data.make_model || '',
                ownerName: data.owner_name || ''
              };
              
              // Check if current customer is the owner (if customer already filled in)
              if (prev.name && data.owner_name) {
                const isOwner = prev.name.toLowerCase().trim() === data.owner_name.toLowerCase().trim();
                newData.isOwner = isOwner;
                
                if (isOwner) {
                  toast({
                    title: "Staðfest",
                    description: "Þú ert skráður eigandi þessa bíls",
                  });
                }
              }
              
              return newData;
            });
            
            // Set suggested vehicle size from backend
            if (data.suggested_size) {
              setVehicleSize(data.suggested_size);
              
              // Map size codes to readable names
              const sizeNames = {
                'smabill': 'Smábíll',
                'folksbill': 'Fólksbíll',
                'stor_folksbill': 'Stór fólksbíll',
                'litill_jeppi': 'Lítill jeppi',
                'stor_jeppi': 'Stór jeppi',
                'breyttir_jeppar': 'Breyttir jeppar',
                'litill_sendibill': 'Lítill sendibíll',
                'sendibill': 'Sendibíll',
                'stor_sendibill': 'Stór sendibíll',
                '9_manna': '9 manna'
              };
              
              toast({
                title: "Bílastærð sjálfvirk",
                description: `Greint sem: ${sizeNames[data.suggested_size] || data.suggested_size}`,
              });
            }
          } else {
            toast({
              title: "Bíll fannst ekki",
              description: "Engin ökutækjaskráning fannst með þetta skráningarmerki.",
              variant: "destructive"
            });
          }
        } else {
          throw new Error('Villa kom upp í bíluppflettingu');
        }
      } catch (error) {
        console.error('Vehicle lookup error:', error);
        setVehicleLookupError(true);
        toast({
          title: 'Villa í bíluppflettingu',
          description: 'Tókst ekki að sækja bíluppplýsingar. Prófaðu aftur síðar.',
          variant: 'destructive',
        });
      } finally {
        setLookingUpPlate(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least phone OR email is provided
    if (!formData.phone && !formData.email) {
      toast({
        title: "Villa",
        description: "Vinsamlegast fylltu út sími eða netfang",
        variant: "destructive"
      });
      return;
    }
    
    // Validate vehicle size is detected
    if (!vehicleSize) {
      toast({
        title: "Villa",
        description: "Ekki tókst að greina bílastærð. Vinsamlegast reyndu aftur.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);

    try {
      const bookingData = {
        services: selectedServices.map((id) => ({
          service_id: id,
          quantity: 1
        })),
        date: selectedDate.toISOString().split('T')[0], // Convert Date to YYYY-MM-DD
        time: selectedTime,
        customer_name: formData.name || "Ónafngreindur viðskiptavinur",
        customer_email: formData.email || "",
        customer_phone: formData.phone || "",
        customer_kennitala: formData.kennitala || "",
        plate_number: formData.plate.toUpperCase(),
        vehicle_size: vehicleSize,
        payment_method: formData.paymentMethod,
        send_sms: false,
        notes: formData.notes
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const result = await response.json();
        setUser({ name: formData.name, email: formData.email, phone: formData.phone });
        localStorage.setItem('user', JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone }));
        
        toast({
          title: "Bókun tókst!",
          description: `Bókun nr. ${result.booking_id} - Verð: ${result.total?.toLocaleString()} kr.`,
        });

        // If online payment and customer is NOT the owner, ask who pays
        if (formData.paymentMethod === 'online') {
          // CRM should have returned payment_link
          if (result.payment_link) {
            // Store booking_id in sessionStorage for when customer returns
            sessionStorage.setItem('pending_booking_id', result.booking_id);
            // Redirect to Rapyd payment
            window.location.href = result.payment_link;
          } else {
            // No payment link - show message and redirect to confirmation with warning
            toast({
              title: "Athugið",
              description: "Online greiðsla er ekki tiltæk. Þú getur greitt á staðnum.",
              variant: "destructive"
            });
            
            // Navigate to confirmation with warning
            navigate('/confirmation', { 
              state: { 
                type: 'booking', 
                booking_id: result.booking_id,
                total: result.total,
                payment_method: 'on_site',
                warning: 'Online greiðsla var ekki tiltæk. Vinsamlegast greiddu á staðnum.'
              } 
            });
          }
        } else {
          // On-site payment - owner pays on site
          navigate('/confirmation', { 
            state: { 
              type: 'booking', 
              booking_id: result.booking_id,
              total: result.total,
              payment_method: 'on_site'
            } 
          });
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Booking failed');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: "Villa kom upp",
        description: error.message || "Ekki tókst að bóka tíma",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-12 pb-32">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)] items-start mb-10">
          <div className="text-left md:text-left space-y-3">
            <p className="inline-flex items-center rounded-full bg-[#E8F4F8] px-3 py-1 text-xs font-medium text-[#0A4D8C] border border-[#BFDFF1]">
              <Sparkles className="mr-1 h-3 w-3" />
              Auðvelt bókunarflæði í þremur skrefum
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C3E50] tracking-tight"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Bóka tíma fyrir bílinn þinn
            </h1>
            <p className="text-sm md:text-base text-[#8B9BA8] max-w-xl">
              Sláðu inn bílinn, veldu þjónustu og bókaðu tíma sem hentar þér. Við sjáum um rest.
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2" data-testid="booking-progress">
              <div className="flex items-center gap-2">
                <div className="relative h-9 w-9 rounded-full bg-[#0A4D8C] text-white grid place-items-center text-sm font-semibold">
                  1
                </div>
                <span className="text-xs md:text-sm text-[#2C3E50] font-medium">Tengiliður & bíll</span>
              </div>
              <div className="h-px w-6 bg-[#E8ECEF]" aria-hidden="true" />
              <div className="flex items-center gap-2 opacity-80">
                <div className="h-8 w-8 rounded-full border border-[#E8ECEF] grid place-items-center text-[11px] font-medium text-[#8B9BA8]">
                  2
                </div>
                <span className="text-xs md:text-sm text-[#8B9BA8]">Þjónusta & tími</span>
              </div>
              <div className="h-px w-6 bg-[#E8ECEF]" aria-hidden="true" />
              <div className="flex items-center gap-2 opacity-80">
                <div className="h-8 w-8 rounded-full border border-[#E8ECEF] grid place-items-center text-[11px] font-medium text-[#8B9BA8]">
                  3
                </div>
                <span className="text-xs md:text-sm text-[#8B9BA8]">Staðfesting</span>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-[#E8ECEF] shadow-md p-5 space-y-3" data-testid="booking-summary-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#8B9BA8] mb-1 flex items-center gap-1">
                  <Car className="h-3 w-3" /> Bíll
                </p>
                <p className="text-sm font-semibold text-[#2C3E50] line-clamp-1">
                  {formData.vehicle || 'Bíll ekki skráður enn'}
                </p>
                <p className="text-xs text-[#8B9BA8]">
                  {formData.plate ? formData.plate.toUpperCase() : 'Skráningarmerki vantar'}
                </p>
              </div>
            </div>

            <div className="h-px bg-[#E8ECEF]" />

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-[#8B9BA8] mb-1 flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> Valin tímasetning
              </p>
              <p className="text-sm font-medium text-[#2C3E50]">
                {selectedDate
                  ? selectedDate.toLocaleDateString('is-IS', { weekday: 'long', day: '2-digit', month: '2-digit' })
                  : 'Engin dagsetning valin'}
              </p>
              <p className="text-xs text-[#8B9BA8]">
                {selectedTime || 'Veldu lausan tíma hér fyrir neðan'}
              </p>
            </div>

            <div className="h-px bg-[#E8ECEF]" />

            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-[#8B9BA8] mb-1">Þjónusta & verð</p>
              <p className="text-sm font-medium text-[#2C3E50]">
                {selectedService
                  ? services.find((s) => s.id === selectedService)?.name || 'Valin þjónusta'
                  : 'Engin þjónusta valin'}
              </p>
              {extraServices.length > 0 && (
                <p className="text-xs text-[#8B9BA8]">
                  + viðbótarþjónusta: {extraServices
                    .map((id) => services.find((s) => s.id === id)?.name)
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              <p className="text-xs text-[#8B9BA8]">
                {pricingStatus === 'ok' && pricing?.price
                  ? `Áætlað verð fyrir aðalþjónustu: ${pricing.price.toLocaleString()} kr. (viðbótarþjónusta getur bæst við á staðnum)`
                  : 'Verð miðast við bílastærð og þjónustu. Staðfest á staðnum ef þarf.'}
              </p>
            </div>
          </div>
        </div>

        <Card className="border-[#E8ECEF] shadow-lg" data-testid="booking-form-card">
          <CardHeader>
            <CardTitle className="text-2xl text-[#2C3E50]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Bókunarfórm
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Vehicle Info - fyrst í flæði */}
              <div className="bg-[#E8F4F8] p-6 rounded-lg border-2 border-[#4A9FD8]">
                <h3 className="text-lg font-semibold text-[#2C3E50] mb-1 flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[#0A4D8C] text-xs font-semibold">
                    <Car className="h-3 w-3" />
                  </span>
                  Bílaupplýsingar
                </h3>
                <p className="text-xs text-[#2C3E50]/80 mb-4">
                  Sláðu inn skráningarmerki til að sækja upplýsingar um bílinn sjálfkrafa.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="plate" className="text-[#2C3E50] flex items-center justify-between">
                      <span>Skráningarmerki *</span>
                      <span className="text-xs font-normal text-[#8B9BA8]">3+ stafir, t.d. AB123</span>
                    </Label>
                    <Input 
                      id="plate"
                      value={formData.plate}
                      onChange={handlePlateChange}
                      onBlur={handlePlateBlur}
                      placeholder="AB123"
                      className="mt-2 uppercase"
                      required
                      data-testid="booking-plate-input"
                    />
                    {lookingUpPlate && (
                      <p
                        className="text-xs text-[#8B9BA8] mt-1 flex items-center gap-1"
                        data-testid="booking-plate-loading"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" /> Leita...
                      </p>
                    )}
                    {vehicleLookupError && !lookingUpPlate && (
                      <p
                        className="text-xs text-[#EF4444] mt-1"
                        data-testid="booking-plate-error"
                      >
                        Villa kom upp við að sækja bíluppplýsingar. Prófaðu aftur síðar eða sláðu inn bíllýsinguna handvirkt.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="vehicle" className="text-[#2C3E50]">Bíll *</Label>
                    <Input 
                      id="vehicle"
                      value={formData.vehicle}
                      onChange={(e) => setFormData({...formData, vehicle: e.target.value})}
                      placeholder="t.d. Toyota Yaris 2020"
                      className="mt-2"
                      required
                      disabled={lookingUpPlate}
                      data-testid="booking-vehicle-input"
                    />
                    {formData.vehicle && !lookingUpPlate && (
                      <p
                        className="text-xs text-[#10B981] mt-1 flex items-center gap-1"
                        data-testid="booking-vehicle-found"
                      >
                        <Check className="w-3 h-3" /> Fundið
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Simplified Contact Info – neðst í flæði */}
              <div className="bg-[#F8FAFB] p-6 rounded-lg border border-[#E8ECEF]">
                <h3 className="text-lg font-semibold text-[#2C3E50] mb-1">Tengiliðaupplýsingar</h3>
                <p className="text-xs text-[#8B9BA8] mb-4">
                  Við notum aðeins upplýsingarnar til að staðfesta bókun og senda staðfestingu.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-[#2C3E50] flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#E8F4F8] text-[#0A4D8C] text-[11px] font-semibold">
                        Sími
                      </span>
                    </Label>
                    <Input 
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="t.d. 588 0705"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#2C3E50] flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#E8F4F8] text-[#0A4D8C] text-[11px] font-semibold">
                        Netfang
                      </span>
                    </Label>
                    <Input 
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="t.d. nafn@example.is"
                      className="mt-2"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#8B9BA8] mt-2">* Þú þarft aðeins að fylla út annað hvort símanúmer eða netfang.</p>
              </div>
 

              {/* Service Selection */}
              <div>
                <Label htmlFor="service" className="text-[#2C3E50]">Aðalþjónusta *</Label>
                <Select value={selectedService} onValueChange={setSelectedService} required>
                  <SelectTrigger id="service" className="mt-2" data-testid="booking-service-select">
                    <SelectValue placeholder="Veldu þjónustu" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id} data-testid={`booking-service-option-${service.id}`}>
                        <div className="flex flex-col">
                          <span className="font-medium text-[#2C3E50] line-clamp-1">{service.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Virkt verð fyrir valinn bíl + þjónustu */}
                {pricingStatus === 'loading' && (
                  <div className="mt-3 text-xs text-[#8B9BA8]" data-testid="booking-pricing-loading">
                    Sæki verðskrárupplýsingar...
                  </div>
                )}

                {pricingStatus === 'ok' && pricing && pricing.price && (
                  <div className="mt-3 p-4 bg-[#10B981]/5 border border-[#10B981]/60 rounded-lg flex items-center justify-between" data-testid="booking-selected-price">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[#8B9BA8]">Áætlað verð</p>
                      <p className="text-base font-semibold text-[#10B981]">
                        {pricing.price?.toLocaleString()} kr.
                      </p>
                    </div>
                    <p className="text-xs text-[#8B9BA8] text-right max-w-[200px]">
                      Endanlegt verð getur tekið mið af ástandi bíls og viðbótarþjónustu.
                    </p>
                  </div>
                )}

                {pricingStatus === 'not_found' && (
                  <div className="mt-3 p-3 bg-[#FEF3C7] border border-[#F59E0B] rounded-lg" data-testid="booking-pricing-missing">
                    <p className="text-xs text-[#92400E]">
                      Verð fyrir þessa samsetningu (þjónusta + bílastærð) hefur ekki enn verið skráð í CRM.
                      Hafðu samband við Alvörubón ef þú vilt fá verðtilboð.
                    </p>
                  </div>
                )}

                {pricingStatus === 'error' && (
                  <div className="mt-3 p-3 bg-[#FEE2E2] border border-[#EF4444] rounded-lg" data-testid="booking-pricing-error">
                    <p className="text-xs text-[#991B1B]">
                      Ekki tókst að sækja verðskrá þessa stundina. Þú getur samt bókað tíma og fengið verð
                      staðfest á staðnum eða í síma.
                    </p>
                  </div>
                )}

                {/* Extra Services (upsell) - Birtist ef valin þjónusta hefur config */}
                {selectedService && EXTRA_SERVICE_CONFIG[selectedService] && (
                  <div className="mt-6 p-4 bg-[#F0F9FF] border border-[#4A9FD8]/30 rounded-lg" data-testid="booking-extra-services">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-[#0A4D8C]" />
                      <h4 className="text-sm font-semibold text-[#2C3E50]">Bæta við viðbótarþjónustu</h4>
                    </div>
                    <p className="text-xs text-[#8B9BA8] mb-3">
                      Við mælum með þessum viðbótarþjónustum út frá valinni aðalþjónustu
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {EXTRA_SERVICE_CONFIG[selectedService].map((serviceId) => {
                        const service = services.find((s) => s.id === serviceId);
                        if (!service) return null;
                        
                        const isSelected = extraServices.includes(serviceId);
                        const priceData = extraServicePricing[serviceId];
                        
                        return (
                          <button
                            key={serviceId}
                            type="button"
                            onClick={() => toggleExtraService(serviceId)}
                            data-testid={`extra-service-chip-${serviceId}`}
                            className={[
                              'inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all duration-150',
                              'border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A4D8C]',
                              isSelected
                                ? 'border-[#0A4D8C] bg-[#0A4D8C] text-white shadow-sm'
                                : 'border-[#E8ECEF] bg-white text-[#2C3E50] hover:border-[#0A4D8C]/70 hover:bg-[#E8F4F8]',
                            ].join(' ')}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                            <span className="flex items-center gap-1.5">
                              <span>{service.name}</span>
                              {priceData && priceData.price && (
                                <span className={isSelected ? 'text-white/90' : 'text-[#0A4D8C]'}>
                                  • {priceData.price.toLocaleString()} kr.
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Discount message */}
                    {MOCK_BUNDLE_DISCOUNTS[selectedService] && (
                      <div className="mt-3 p-3 bg-[#10B981]/5 border border-[#10B981]/30 rounded-lg flex items-start gap-2" data-testid="booking-discount-badge">
                        <Tag className="h-4 w-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-[#10B981]">
                            {MOCK_BUNDLE_DISCOUNTS[selectedService].discount_message}
                          </p>
                          <p className="text-xs text-[#8B9BA8] mt-1">
                            MOCK tilboð - CRM útfærsla í vinnslu
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Heildarverð með discount */}
                    {extraServices.length > 0 && (() => {
                      const { subtotal, discount, discountPercent, finalTotal, hasAllPrices } = calculateTotalPrice();
                      return hasAllPrices ? (
                        <div className="mt-4 pt-3 border-t border-[#4A9FD8]/20" data-testid="booking-total-price">
                          {/* Discount active */}
                          {discount > 0 ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#8B9BA8]">Upprunalegt verð:</span>
                                <span className="text-[#8B9BA8] line-through">{subtotal.toLocaleString()} kr.</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#10B981] font-medium flex items-center gap-1">
                                  <Tag className="h-3.5 w-3.5" />
                                  Afsláttur ({discountPercent}%):
                                </span>
                                <span className="text-[#10B981] font-medium">-{discount.toLocaleString()} kr.</span>
                              </div>
                              <div className="pt-2 border-t border-[#E8ECEF]">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-wide text-[#8B9BA8]">Lokaverð</p>
                                    <p className="text-xs text-[#10B981] mt-0.5 font-medium">
                                      💰 Þú sparar {discount.toLocaleString()} kr.!
                                    </p>
                                  </div>
                                  <p className="text-2xl font-bold text-[#0A4D8C]">
                                    {finalTotal.toLocaleString()} kr.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* No discount */
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-[#8B9BA8]">Áætlað heildarverð</p>
                                <p className="text-xs text-[#8B9BA8] mt-0.5">
                                  Aðalþjónusta ({pricing?.price?.toLocaleString()} kr.) + {extraServices.length} viðbótarþjónusta
                                </p>
                              </div>
                              <p className="text-xl font-bold text-[#0A4D8C]">
                                {finalTotal.toLocaleString()} kr.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              {/* Date and Time Selection */}
              <div className="bg-[#F8FAFB] p-6 rounded-lg border border-[#E8ECEF]">
                <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Veldu dag og tíma</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  {/* Calendar vinstra megin */}
                  <div>
                    <Label className="text-[#2C3E50] mb-2 block">Dagsetning *</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        const twoDaysAhead = new Date(today);
                        twoDaysAhead.setDate(today.getDate() + 14);  // Increased to 14 days for testing

                        return date < today || date > twoDaysAhead;
                      }}
                      className="rounded-md border"
                    />
                  </div>

                  {/* Tímar hægra megin */}
                  <div>
                    {selectedDate ? (
                      <>
                        <Label className="text-[#2C3E50] mb-3 block">
                          Lausir tímar {selectedDate.toLocaleDateString('is-IS')} *
                        </Label>

                        <DataBoundary
                          isLoading={loadingAvailability}
                          isError={availabilityError}
                          isEmpty={!loadingAvailability && !availabilityError && availability.length === 0}
                          skeleton={(
                            <div
                              className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                              data-testid="booking-slots-skeleton"
                            >
                              {Array.from({ length: 9 }).map((_, index) => (
                                <div
                                  key={index}
                                  className="h-10 rounded-full skeleton-shimmer"
                                />
                              ))}
                            </div>
                          )}
                          emptyProps={{
                            icon: 'search',
                            title: 'Engir lausir tímar',
                            description: 'Reyndu aðra dagsetningu eða hafðu samband við Alvörubón.',
                            'data-testid': 'empty-slots',
                          }}
                          errorProps={{
                            title: 'Tókst ekki að sækja lausa tíma',
                            description: 'Athugaðu nettengingu eða reyndu aftur síðar.',
                            onRetry: () => {
                              if (selectedDate) {
                                const dateStr = selectedDate.toISOString().split('T')[0];
                                fetchAvailability(dateStr);
                              }
                            },
                            'data-testid': 'error-slots',
                          }}
                        >
                          <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {availability.map((slot) => {
                              const isSelected = selectedTime === slot.time;
                              const isDisabled = !slot.available;

                              return (
                                <button
                                  key={slot.time}
                                  type="button"
                                  onClick={() => !isDisabled && setSelectedTime(slot.time)}
                                  disabled={isDisabled}
                                  data-testid="booking-time-slot-pill"
                                  className={[
                                    'relative flex h-10 items-center justify-center rounded-full border text-xs font-medium transition-colors duration-150 px-3',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A4D8C]',
                                    isDisabled
                                      ? 'border-[#E5E7EB] bg-[#F9FAFB] text-[#D1D5DB] cursor-not-allowed'
                                      : isSelected
                                      ? 'border-transparent bg-[#0A4D8C] text-white shadow-sm hover:bg-[#083A6D]'
                                      : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:border-[#0A4D8C]/70 hover:text-[#111827]',
                                  ].join(' ')}
                                >
                                  <span>{slot.time}</span>
                                  {isSelected && !isDisabled && (
                                    <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[#10B981] border border-white" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </DataBoundary>
                      </>
                    ) : (
                      <p className="text-sm text-[#8B9BA8] mt-1">
                        Vinsamlegast veldu dagsetningu til að sjá lausa tíma
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="text-[#2C3E50]">Athugasemdir (valfrjálst)</Label>
                <Textarea 
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="mt-2"
                  rows={3}
                />
              </div>

              {/* Payment Method */}
              <div className="bg-[#F8FAFB] p-6 rounded-lg border border-[#E8ECEF]" data-testid="booking-payment-method">
                <h3 className="text-lg font-semibold text-[#2C3E50] mb-2">Greiðslumáti</h3>
                <p
                  className="text-xs text-[#8B9BA8] mb-3"
                  data-testid="pay-now-inline-helper"
                >
                  Þú færð alltaf bókunarstaðfestingu núna. Ef greiðslulink vantar greiðirðu einfaldlega á staðnum.
                </p>
                <RadioGroup
                  value={formData.paymentMethod}
                  onValueChange={(value) => setFormData({ ...formData, paymentMethod: value })}
                  className="space-y-3"
                  data-testid="payment-method-radiogroup"
                >
                  <label
                    className={[
                      'flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors duration-150',
                      formData.paymentMethod === 'on_site'
                        ? 'border-[#0A4D8C] bg-white shadow-sm'
                        : 'border-[#E8ECEF] bg-[#F9FAFB] hover:border-[#0A4D8C]/70',
                    ].join(' ')}
                  >
                    <RadioGroupItem
                      value="on_site"
                      id="payment-on-site"
                      className="mr-3"
                      data-testid="payment-method-on-site"
                    />
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#0A4D8C]/10 text-[#0A4D8C] grid place-items-center">
                        <Wallet className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-[#2C3E50]">Greiða á staðnum</p>
                        <p className="text-sm text-[#8B9BA8]">Greiðslukort eða reiðufé við afhendingu</p>
                      </div>
                    </div>
                  </label>

                  <label
                    className={[
                      'flex items-center p-4 rounded-xl border-2 cursor-pointer transition-colors duration-150',
                      formData.paymentMethod === 'online'
                        ? 'border-[#0A4D8C] bg-white shadow-sm'
                        : 'border-[#E8ECEF] bg-[#F9FAFB] hover:border-[#0A4D8C]/70',
                    ].join(' ')}
                  >
                    <RadioGroupItem
                      value="online"
                      id="payment-online"
                      className="mr-3"
                      data-testid="payment-method-online"
                    />
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#0A4D8C]/10 text-[#0A4D8C] grid place-items-center">
                        <CreditCard className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-[#2C3E50] flex items-center gap-1">
                          Greiða núna
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-full p-0.5 text-[#8B9BA8] hover:text-[#0A4D8C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A4D8C]"
                                  aria-label="Upplýsingar um greiðslu á netinu"
                                  data-testid="pay-now-tooltip-trigger"
                                >
                                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">
                                Netgreiðsla er ekki fullvirk enn. Greiðslutengill (payment_link) getur vantað frá CRM og þá greiðirðu á staðnum.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </p>
                        <p className="text-sm text-[#8B9BA8]">Greiðslukort á netinu fyrir bókun (ef í boði)</p>
                      </div>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              <Button 
                type="submit" 
                disabled={loading || !selectedService || !selectedDate || !selectedTime || !vehicleSize}
                className="w-full bg-[#0A4D8C] hover:bg-[#083A6B] text-white py-6 text-lg rounded-lg"
                data-testid="submit-booking-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Bóka...
                  </>
                ) : (
                  <>
                    <Calendar className="mr-2 h-5 w-5" />
                    Staðfesta Bókun
                  </>
                )}
              </Button>

              {/* Staðsetning & hafa samband */}
              <div className="bg-white p-6 rounded-lg border border-[#E8ECEF]" data-testid="booking-location-box">
                <h3 className="text-lg font-semibold text-[#2C3E50] mb-2">Staðsetning</h3>
                <p className="text-sm text-[#8B9BA8] mb-3">
                  Alvörubón ehf., Viðarhöfða 2a, 110 Reykjavík. Mættu 5–10 mínútum fyrir bókaðan tíma.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="text-sm text-[#2C3E50]">
                    <p className="font-medium mb-1">Hafðu samband</p>
                    <p className="text-[#8B9BA8]">
                      Sími: <a href="tel:5880705" className="hover:text-[#0A4D8C] underline-offset-2 hover:underline">588-0705</a><br />
                      Neyð/utan opnunartíma: <a href="tel:7835000" className="hover:text-[#0A4D8C] underline-offset-2 hover:underline">783-5000</a><br />
                      Netfang: <a href="mailto:alvorubon@alvorubon.is" className="hover:text-[#0A4D8C] underline-offset-2 hover:underline">alvorubon@alvorubon.is</a>
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden border border-[#E8ECEF]">
                    <iframe
                      title="Staðsetning Alvörubón ehf."
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d641.2684301571958!2d-21.839!3d64.135!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNjTCsDA4JzA2LjAiTiAyMcKwNTAnMTkuMCJX!5e0!3m2!1sen!2sis!4v1700000000000"
                      width="100%"
                      height="180"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default BookingPage;
